// Adım 2 — "Önerilen kırılımlar" (spec §7.3, DR7: kart YOK, tek başlık altında ince çizgili yoğun
// tablo). Sütunlar Üst gösterge | Grup | Üyeler | Tamlık (yıl başına) | Hesaplama rolü | Karar;
// durumlar Önerildi / Kabul edildi / Atlandı (grup bazında, geri alınabilir); toplu kapsam
// "Dosyadaki N öneri". "Dosyadaki toplam eşleşti" (otomatik, dosyadan) ile "Bu üyeler tüm kapsamı
// temsil ediyor" (kullanıcı onayı) AYRI satırlardır. Karar mantığı lib/kpi-import-breakdown'da.

import {
  type BreakdownSuggestion,
  type SuggestionDecision,
  type SuggestionState,
} from "../../../lib/kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import { btnSmall, monoStyle, pillStyle, tdStyle, thStyle } from "../kpi-form-styles";

const b = KPI_IMPORT_COPY.breakdown;
const btn: React.CSSProperties = { ...btnSmall, minHeight: 44 };

function statePill(state: SuggestionState): JSX.Element {
  const style: React.CSSProperties =
    state === "accepted"
      ? pillStyle
      : state === "skipped"
        ? { ...pillStyle, background: "var(--color-bg)", color: "var(--color-text-3)" }
        : {
            ...pillStyle,
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
          };
  return (
    <span style={style} data-testid="kpi-import-bd-state">
      {b.state[state]}
    </span>
  );
}

export interface ImportBreakdownSuggestionsProps {
  suggestions: readonly BreakdownSuggestion[];
  onDecide: (keys: ReadonlySet<string>, decision: SuggestionDecision) => void;
  onComplete: (key: string, complete: boolean) => void;
}

export function ImportBreakdownSuggestions(p: ImportBreakdownSuggestionsProps): JSX.Element | null {
  const { suggestions, onDecide, onComplete } = p;
  if (suggestions.length === 0) return null;
  const all = new Set(suggestions.map((s) => s.key));
  const allAccepted = suggestions.every((s) => s.state === "accepted");
  const allSkipped = suggestions.every((s) => s.state === "skipped");
  return (
    <section aria-labelledby="kpi-import-bd-title" data-testid="kpi-import-bd-suggestions">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <h3 id="kpi-import-bd-title" className="font-semibold" style={{ margin: 0 }}>
          {b.suggestionsTitle}
        </h3>
        <span style={{ color: "var(--color-text-3)", fontSize: "0.875rem" }}>
          {b.scope(suggestions.length)}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          style={btn}
          disabled={allAccepted}
          onClick={() => onDecide(all, "accept")}
        >
          {b.acceptAll}
        </button>
        <button
          type="button"
          style={btn}
          disabled={allSkipped}
          onClick={() => onDecide(all, "skip")}
        >
          {b.skipAll}
        </button>
      </div>
      <div className="text-sm mb-2" style={{ color: "var(--color-text-2)" }}>
        {b.suggestionsHint}
      </div>
      <div style={{ overflowX: "auto", borderTop: "1px solid var(--color-border)" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 960 }}>
          <thead>
            <tr>
              <th style={thStyle} scope="col">
                {b.th.parent}
              </th>
              <th style={thStyle} scope="col">
                {b.th.group}
              </th>
              <th style={thStyle} scope="col">
                {b.th.members}
              </th>
              <th style={thStyle} scope="col">
                {b.th.completeness}
              </th>
              <th style={thStyle} scope="col">
                {b.th.role}
              </th>
              <th style={thStyle} scope="col">
                {b.th.decision}
              </th>
            </tr>
          </thead>
          <tbody>
            {suggestions.map((s) => (
              <SuggestionRow key={s.key} s={s} onDecide={onDecide} onComplete={onComplete} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SuggestionRow(x: {
  s: BreakdownSuggestion;
  onDecide: ImportBreakdownSuggestionsProps["onDecide"];
  onComplete: ImportBreakdownSuggestionsProps["onComplete"];
}): JSX.Element {
  const { s } = x;
  const keys = new Set([s.key]);
  const skipped = s.state === "skipped";
  const completeId = `kpi-import-bd-complete-${s.key.replace(/[^a-z0-9_]/giu, "_")}`;
  return (
    <tr style={skipped ? { opacity: 0.7 } : undefined} data-testid="kpi-import-bd-row">
      <td style={tdStyle}>
        <div>{s.parentLabel}</div>
        <div style={{ ...monoStyle, fontSize: "0.75rem", color: "var(--color-text-3)" }}>
          {s.parentCode}
        </div>
      </td>
      <td style={tdStyle}>
        <div>{s.groupLabel}</div>
        <div style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
          {s.existingGroup ? b.existingGroup : b.newGroup}
        </div>
      </td>
      <td style={{ ...tdStyle, maxWidth: 260 }}>
        <span style={{ overflowWrap: "anywhere" }}>{s.members.join(", ")}</span>
      </td>
      <td style={tdStyle}>
        <div data-testid="kpi-import-bd-years">
          {s.columns.length > 0
            ? s.columns.map((c) => b.yearCheck(c.label, c.matched)).join(" · ")
            : b.noTotalRow}
        </div>
        {s.totalMatched ? (
          <div
            style={{ fontSize: "0.75rem", color: "var(--color-text-2)" }}
            data-testid="kpi-import-bd-total-matched"
          >
            ✓ {b.totalMatched}
          </div>
        ) : null}
        <label
          htmlFor={completeId}
          className="flex items-center gap-2"
          style={{ fontSize: "0.75rem", color: "var(--color-text-2)", minHeight: 44 }}
          title={s.existingGroup ? b.existingGroupHint : undefined}
        >
          <input
            id={completeId}
            type="checkbox"
            checked={s.complete}
            disabled={s.existingGroup || skipped}
            onChange={(e) => x.onComplete(s.key, e.target.checked)}
          />
          {b.representsAll}
        </label>
        {!s.existingGroup ? (
          <div style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
            {b.representsAllHint}
          </div>
        ) : null}
      </td>
      <td style={tdStyle}>{b.role[s.role]}</td>
      <td style={tdStyle}>
        <div className="flex items-center gap-2 flex-wrap">
          {statePill(s.state)}
          {s.state === "suggested" ? (
            <>
              <button
                type="button"
                style={btn}
                aria-label={`${b.accept}: ${s.parentLabel} › ${s.groupLabel}`}
                onClick={() => x.onDecide(keys, "accept")}
              >
                {b.accept}
              </button>
              <button
                type="button"
                style={btn}
                aria-label={`${b.skip}: ${s.parentLabel} › ${s.groupLabel}`}
                onClick={() => x.onDecide(keys, "skip")}
              >
                {b.skip}
              </button>
            </>
          ) : (
            <button
              type="button"
              style={btn}
              aria-label={`${b.undo}: ${s.parentLabel} › ${s.groupLabel}`}
              onClick={() => x.onDecide(keys, "undo")}
            >
              {b.undo}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
